import { useEffect, useMemo, useState } from "react";
import TagBox from "devextreme-react/tag-box";
import Button from "devextreme-react/button";
import CheckBox from "devextreme-react/check-box";
import { AppName } from "../../constants";
import { createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { SiteSelectionPopup } from "../scheduleFiles/SiteSelectionPopup";
import "./sitenotification.scss";

export interface Associations {
    Types: string[];
    Templates: string[];
    Sites: number[];
    AllSites: boolean;
}

const typeOptions = Object.values(AppName);

const toStringArray = (values: any): string[] => Array.isArray(values)
    ? values.map((value) => String(value)).filter(Boolean)
    : [];

const toNumberArray = (values: any): number[] => Array.isArray(values)
    ? values.map((value) => Number(value)).filter((value) => !Number.isNaN(value))
    : [];

export const parseAssociations = (associations?: string | null): Associations => {
    if (!associations) return { Types: [], Templates: [], Sites: [], AllSites: false };
    try {
        const parsed = JSON.parse(associations);
        return {
            Types: toStringArray(parsed?.Types),
            Templates: toStringArray(parsed?.Templates),
            Sites: toNumberArray(parsed?.Sites),
            AllSites: parsed?.AllSites === true,
        };
    } catch {
        return { Types: [], Templates: [], Sites: [], AllSites: false };
    }
};

export const formatAssociations = (associations?: string | null) => {
    const parsed = parseAssociations(associations);
    const summary = (Object.entries({
        Types: parsed.Types,
        Templates: parsed.Templates,
        Sites: parsed.AllSites ? ["All"] : parsed.Sites,
    }) as [string, (string | number)[]][])
        .filter(([, values]) => values.length > 0)
        .map(([key, values]) => `${key}-${values.length > 1 ? "<Multiple>" : values[0]}`);
    return summary.length > 0 ? summary.join(", ") : "NA";
};

interface AssociationsEditorProps {
    value?: string | null;
    typeOptions?: string[];
    onChange: (associations: string) => void;
}

const AssociationsEditor = ({ value, typeOptions = Object.values(AppName), onChange }: AssociationsEditorProps) => {

    const associations = useMemo(() => parseAssociations(value), [value]);
    const [templateOptions, setTemplateOptions] = useState<string[]>([]);
    const [isSitePopupOpen, setIsSitePopupOpen] = useState(false);
    const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);

    const selectedTypes = useMemo(
        () => associations.Types.join("|"),
        [associations.Types]
    );

    useEffect(() => {
        if (associations.Types.length === 0) {
            setTemplateOptions([]);
            setIsTemplatesLoading(false);
            return;
        }

        let isCurrent = true;
        const fetchTemplates = async () => {
            setIsTemplatesLoading(true);

            try {
                const templateResults = await Promise.all(
                    associations.Types.map((appName) =>
                        createSitesClientApi.highQ_GetHQSiteTemplate(appName),
                    ),
                );

                const templates = templateResults.flat();
                if (!isCurrent) return;

                setTemplateOptions(
                    Array.from(
                        new Set(
                            templates
                                .map((template) => template.sitename ?? "")
                                .filter(Boolean),
                        ),
                    ),
                );
            } catch (error) {
                if (!isCurrent) return;
                console.error(error);
                setTemplateOptions([]);
            } finally {
                if (isCurrent) setIsTemplatesLoading(false);
            }
        };

        fetchTemplates();
        return () => {
            isCurrent = false;
        };
    }, [selectedTypes]);

    const emit = (changes: Partial<Associations>) => {
        const next = JSON.stringify({ ...associations, ...changes });
        if (next === JSON.stringify(associations)) return;
        onChange(next);
    };

    const selectedSitesText = associations.Sites.length > 0
        ? associations.Sites.join(", ")
        : "";

    return (
        <div className="associations-editor">
            <div className="associations-editor__field">
                <label className="associations-editor__label">
                    Types
                </label>

                <TagBox
                    dataSource={typeOptions}
                    value={associations.Types}
                    showSelectionControls={true}
                    applyValueMode="instantly"
                    searchEnabled={true}
                    placeholder="Select types"
                    width="100%"
                    onValueChanged={(event) => {
                        const types = toStringArray(event.value);
                        const removedType = associations.Types.some((type) => !types.includes(type));
                        emit(types.length === 0 || removedType
                            ? { Types: types, Templates: [], Sites: [], AllSites: false }
                            : { Types: types });
                    }}
                />
            </div>

            <div className="associations-editor__field">
                <label className="associations-editor__label">
                    Templates
                </label>

                <TagBox
                    dataSource={templateOptions}
                    value={associations.Templates}
                    showSelectionControls={true}
                    applyValueMode="instantly"
                    searchEnabled={true}
                    disabled={isTemplatesLoading}
                    placeholder={
                        isTemplatesLoading
                            ? "Loading templates..."
                            : "Select templates"
                    }
                    noDataText={
                        isTemplatesLoading
                            ? "Loading templates..."
                            : "No data to display"
                    }
                    width="100%"
                    onValueChanged={(event) =>
                        emit({ Templates: toStringArray(event.value) })
                    }
                />
            </div>

            <div className="associations-editor__field">
                <div className="associations-editor__header">
                    <label className="associations-editor__label">
                        Sites
                    </label>

                    <CheckBox
                        text="All Sites"
                        value={associations.AllSites}
                        onValueChanged={(event) =>
                            emit({
                                AllSites: event.value === true,
                                Sites:
                                    event.value === true
                                        ? []
                                        : associations.Sites,
                            })
                        }
                    />
                </div>

                <div className="associations-editor__site-container">
                    <div className="selected-sites-display">
                        {associations.AllSites
                            ? "All Sites"
                            : selectedSitesText || "No sites selected"}
                    </div>

                    <Button
                        icon="search"
                        stylingMode="outlined"
                        disabled={associations.AllSites}
                        onClick={() => setIsSitePopupOpen(true)}
                    />
                </div>
            </div>

            <SiteSelectionPopup
                isOpen={isSitePopupOpen}
                preSelectedIds={associations.Sites}
                appName={(associations.Types ?? []).join(", ")}
                selectedTemplates={associations.Templates}
                onClose={() => setIsSitePopupOpen(false)}
                onConfirm={(selectedIds) => {
                    emit({ Sites: selectedIds });
                    setIsSitePopupOpen(false);
                }}
            />
        </div>
    );
};

export default AssociationsEditor;
